import React, { useEffect, useRef, useState } from "react"
import styled from "styled-components"
import PerfectScrollbar from "react-perfect-scrollbar"
import { breakpoints as bp, GridRoot } from "../components/layout"
import colors, { hexToRgba } from "../utils/colors"

interface Props {
  tableAsString: string
  minWidth?: string
}

const Scrollbar = styled(PerfectScrollbar)`
  &.enable-scroll {
    overflow-x: auto;

    .ps__rail-x {
      opacity: 1;
      background-color: ${hexToRgba(colors.black, 0.06)};
      border-radius: 100px;
      height: 12px;
      margin-bottom: 10px;

      .ps__thumb-x {
        height: 14px;
        border-radius: 7px;
        background-color: ${colors.white};
        box-shadow: 0 2px 9px 0 ${hexToRgba(colors.black, 0.16)};
        bottom: -1px;
      }
    }
  }
`

const Container = styled(GridRoot)<{ width?: number }>`
  > .enable-scroll {
    width: ${props => (props.width ? `${props.width}px` : "auto")};

    &.at-scrollstart {
      .ps__rail-x {
        margin-left: 5px;
        ${props => (props.width ? `width: calc(${props.width}px - 5px) !important;` : "")}
      }
    }
  }
`

const Content = styled.div<{ tableMinWidth: string }>`
  table {
    word-break: break-all;
  }

  &.enable-scroll {
    padding-bottom: 4em;

    table {
      min-width: ${props => props.tableMinWidth};
    }
  }
`

const ScrollableTable = ({ tableAsString, minWidth }: Props) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [fullWidth, setFullWidth] = useState<number>(0)
  const [windowWidth, setWindowWidth] = useState<number>(0)
  const [atScrollStart, setAtScrollStart] = useState<boolean>(true)
  const tableMinWidth = minWidth || bp.tabletMin
  const enableScrollClass = fullWidth ? "enable-scroll" : ""

  useEffect(() => {
    const initTableScroll = () => {
      setFullWidth(0)

      if (containerRef?.current) {
        const tableRef = containerRef.current.getElementsByTagName("table")?.[0]
        if (tableRef.offsetWidth < parseInt(tableMinWidth, 10)) {
          setFullWidth(tableRef.offsetWidth)
        }
      }
    }
    initTableScroll()

    window.addEventListener("resize", initTableScroll)

    return () => {
      window.removeEventListener("resize", initTableScroll)
    }
  }, [setFullWidth])

  return (
    <Container ignorePadding width={fullWidth}>
      <Scrollbar
        className={`${enableScrollClass} ${atScrollStart ? "at-scrollstart" : ""}`}
        onXReachStart={() => {
          setAtScrollStart(true)
        }}
        onScrollX={() => {
          setAtScrollStart(false)
        }}
      >
        <Content
          className={`${enableScrollClass}`}
          tableMinWidth={tableMinWidth}
          ref={containerRef}
          dangerouslySetInnerHTML={{
            __html: tableAsString,
          }}
        />
      </Scrollbar>
    </Container>
  )
}

export default ScrollableTable
